import { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const usps = [
  {
    number: '01',
    icon: 'ri-timer-flash-line',
    title: 'Rýchlosť',
    headline: 'Web live za 7 dní',
    description:
      'Kým tradičná agentúra ešte schvaľuje briefing, vy už predávate. Môj sprint proces eliminuje zbytočné kolá revízií - dostanete výsledok rýchlo a bez kompromisov na kvalite.',
    tag: 'Rýchlejšie ako agentúry',
    tagIcon: 'ri-rocket-line',
    gradient: 'from-orange-500/10 to-amber-500/5',
    border: 'border-orange-100',
    numColor: 'text-orange-300',
    iconBg: 'bg-gradient-to-br from-orange-500 to-amber-500',
  },
  {
    number: '02',
    icon: 'ri-eye-2-line',
    title: 'Transparentnosť',
    headline: 'Vidíte každý krok',
    description:
      'Žiadne čierne skrinky. Každý míľnik projektu, každá optimalizácia kampane, každé číslo je otvorene zdieľané. Pravidelné reporty bez marketingovej omáčky - len fakty a čísla.',
    tag: 'Nulové prekvapenia',
    tagIcon: 'ri-shield-check-line',
    gradient: 'from-zinc-100 to-zinc-50',
    border: 'border-zinc-200',
    numColor: 'text-zinc-300',
    iconBg: 'bg-gradient-to-br from-zinc-700 to-zinc-900',
  },
  {
    number: '03',
    icon: 'ri-line-chart-fill',
    title: 'Výsledky',
    headline: 'Merateľný rast obratu',
    description:
      'Mojím cieľom nie je pekný web - je to váš zisk. Každé rozhodnutie podkladám dátami, každú kampaň optimalizujem na ROI. Sledujeme merateľný rast a prispôsobujeme stratégiu podľa reálnych čísiel.',
    tag: 'Merateľný rast',
    tagIcon: 'ri-bar-chart-2-line',
    gradient: 'from-amber-500/8 to-orange-500/4',
    border: 'border-amber-100',
    numColor: 'text-amber-300',
    iconBg: 'bg-gradient-to-br from-amber-500 to-orange-500',
  },
];

const extras = [
  { icon: 'ri-customer-service-2-line', text: 'Podpora 7 dní v týždni' },
  { icon: 'ri-calendar-check-line', text: 'Dodržujeme termíny' },
  { icon: 'ri-hand-heart-line', text: 'Platíte za výsledok, nie čas' },
  { icon: 'ri-user-heart-line', text: 'Priamy kontakt - bez prostredníkov' },
  { icon: 'ri-medal-line', text: 'Osobná zodpovednosť za výsledok' },
];

export default function WhyUsSection() {
  const { ref, inView } = useInView();

  return (
    <section className="py-24 bg-zinc-50 relative overflow-hidden">
      {/* Top line */}
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />
      {/* Bottom line */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-zinc-200 to-transparent" />

      {/* Subtle background blobs */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.05) 0%, transparent 65%)' }}
        />
        <div
          className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.04) 0%, transparent 60%)' }}
        />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {/* Header */}
        <div
          ref={ref}
          className={`flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div>
            <div className="inline-flex items-center gap-2 bg-white border border-orange-100 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-flashlight-line text-orange-500 text-sm" />
              <span className="text-orange-600 text-sm font-semibold">Prečo práve my</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-zinc-900 leading-tight">
              Nie som len agentúra.{' '}
              <br className="hidden md:block" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-amber-500">
                Som váš partner.
              </span>
            </h2>
          </div>
          <p className="text-zinc-500 text-base md:text-lg max-w-sm md:text-right leading-relaxed">
            Tri piliere, na ktorých stojí každý projekt,
            ktorý pre vás vytvorím.
          </p>
        </div>

        {/* USP Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {usps.map((usp, i) => (
            <UspCard key={usp.number} usp={usp} delay={i * 110} inView={inView} />
          ))}
        </div>

        {/* Extras strip */}
        <div
          className={`flex flex-wrap items-center justify-center gap-3 transition-all duration-700 delay-500 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
        >
          {extras.map((item) => (
            <div
              key={item.text}
              className="flex items-center gap-2 bg-white border border-zinc-100 rounded-full px-4 py-2.5 group hover:border-orange-200 hover:bg-orange-50 transition-all duration-200 cursor-default"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className={`${item.icon} text-orange-400 group-hover:text-orange-500 text-base transition-colors duration-200`} />
              </div>
              <span className="text-zinc-600 group-hover:text-zinc-800 text-sm font-medium transition-colors duration-200 whitespace-nowrap">{item.text}</span>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}

interface UspCardProps {
  usp: typeof usps[0];
  delay: number;
  inView: boolean;
}

function UspCard({ usp, delay, inView }: UspCardProps) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`relative bg-white border ${usp.border} rounded-2xl p-8 overflow-hidden flex flex-col transition-all duration-500 cursor-default
        ${hovered ? '-translate-y-1.5' : ''}
        ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}
      `}
      style={{ transitionDelay: `${delay}ms` }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Gradient background on hover */}
      <div className={`absolute inset-0 bg-gradient-to-br ${usp.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl`} />
      {/* Always-on subtle gradient */}
      <div className={`absolute inset-0 bg-gradient-to-br ${usp.gradient} opacity-60 rounded-2xl`} />

      <div className="relative z-10 flex flex-col flex-1">
        {/* Number + Icon row */}
        <div className="flex items-start justify-between mb-6">
          <span className={`text-6xl font-black leading-none ${usp.numColor} select-none`}>{usp.number}</span>
          <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${usp.iconBg} transition-transform duration-300 ${hovered ? 'scale-110 rotate-3' : ''}`}>
            <i className={`${usp.icon} text-xl text-white`} />
          </div>
        </div>

        {/* Title */}
        <p className="text-xs font-bold text-orange-500 uppercase tracking-widest mb-1.5">{usp.title}</p>
        <h3 className="text-zinc-900 font-black text-2xl mb-4 leading-tight">{usp.headline}</h3>

        {/* Description */}
        <p className="text-zinc-500 text-sm leading-relaxed flex-1 mb-6">{usp.description}</p>

        {/* Tag */}
        <div className="inline-flex items-center gap-2 bg-zinc-900 rounded-full px-3.5 py-2 self-start">
          <div className="w-4 h-4 flex items-center justify-center">
            <i className={`${usp.tagIcon} text-orange-400 text-xs`} />
          </div>
          <span className="text-white text-xs font-semibold whitespace-nowrap">{usp.tag}</span>
        </div>
      </div>
    </div>
  );
}
